import React, { useState } from 'react';
import './SaveAnswerButton.css';

interface SaveAnswerButtonProps {
  messageId: string;
  conversationId?: string;
  clientId?: string;
  onSave?: (messageId: string, category: string) => void;
  disabled?: boolean;
}

const CATEGORIES = [
  { value: 'technical', label: 'Technical' },
  { value: 'policy', label: 'Policy' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'general', label: 'General' }
];

const SaveAnswerButton: React.FC<SaveAnswerButtonProps> = ({
  messageId,
  conversationId,
  clientId,
  onSave,
  disabled = false
}) => {
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSaveClick = () => {
    if (disabled || saved || saving) return;
    setShowCategoryMenu(true);
  };

  const handleCategorySelect = async (category: string) => {
    if (saving) return;

    setSaving(true);
    setShowCategoryMenu(false);

    try {
      // Call the onSave callback if provided
      if (onSave) {
        await onSave(messageId, category);
      }

      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const authToken = localStorage.getItem('auth_token');
      const guestToken = localStorage.getItem('guest_token');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Step 1: Auto-submit thumbs-up feedback first
      // This ensures the conversation has feedback and appears in the review workflow
      try {
        await fetch(`${baseUrl}/api/feedback`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message_id: messageId,
            conversation_id: conversationId,
            client_id: clientId,
            feedback_type: 'thumbs_up',
            reason: 'Saved for training',
            metadata: {
              guest_token: guestToken,
              auto_feedback: true,
              saved_for_training: true
            }
          }),
        });
      } catch (feedbackError) {
        // Log but don't fail - feedback submission is secondary
        console.warn('Auto feedback submission failed:', feedbackError);
      }

      // Step 2: Save for training
      const response = await fetch(`${baseUrl}/api/feedback/save-for-training`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message_id: messageId,
          conversation_id: conversationId,
          client_id: clientId,
          category: category,
          metadata: {
            guest_token: guestToken
          }
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSaved(true);

      // Reset saved state after 3 seconds
      setTimeout(() => {
        setSaved(false);
      }, 3000);

      return data;
    } catch (error) {
      console.error('Error saving answer for training:', error);
      setSaving(false);
      // Show error message to user
      alert('Failed to save answer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="save-answer-container">
      <button
        className={`save-answer-btn ${saved ? 'saved' : ''} ${saving ? 'saving' : ''}`}
        onClick={handleSaveClick}
        disabled={disabled || saved || saving}
        title="Save this answer for training"
      >
        {saving ? (
          <>⏳ Saving...</>
        ) : saved ? (
          <>✓ Saved</>
        ) : (
          <>💾 Save for Training</>
        )}
      </button>

      {showCategoryMenu && (
        <div className="save-answer-category-menu">
          <div className="category-menu-header">Select Category</div>
          <div className="category-menu-options">
            {CATEGORIES.map((category) => (
              <button
                key={category.value}
                className="category-option"
                onClick={() => handleCategorySelect(category.value)}
                disabled={saving}
              >
                {category.label}
              </button>
            ))}
          </div>
          <button
            className="category-menu-cancel"
            onClick={() => setShowCategoryMenu(false)}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default SaveAnswerButton;

